import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ShieldCheck, Plus, Search, Filter, SlidersHorizontal, Download } from 'lucide-react';
import { RowMenu } from '../components/RowMenu';
import { RefreshButton } from '../_shared/AnalyticsKit';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, FilterBuilder, type ColumnDef, type FilterField, type FilterCondition } from '@vnetwork/ui/data';
import {
  Button, Skeleton, Badge,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
  Input,
  Popover, PopoverTrigger, PopoverContent,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useVerimatrixes,
  useVerimatrixDetail,
  useCreateVerimatrix,
  useUpdateVerimatrix,
  useDeleteVerimatrix,
} from '@/api/media/shared-verimatrix';
import type { VerimatrixDRMConfig } from '@/api/media/configs';
import type { Verimatrix } from '@/api/media/shared-verimatrix-types';
import { ActiveStatusPill } from './ActiveStatusPill';
import styles from './VerimatrixDrmList.module.css';

// ─── Detail dialog ────────────────────────────────────────────────────────────

function VerimatrixDetailDialog({ serviceId, id, config, open, onOpenChange }: {
  serviceId: string; id: number | null; config: VerimatrixDRMConfig;
  open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const { data, isLoading } = useVerimatrixDetail(serviceId, config, id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{config.labels.detailDialogTitle}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading && <div className={styles.loading}>Loading…</div>}
          {!isLoading && data && (
            <div className={styles.detailContent}>
              {([['Name', data.name], ['ID', `#${data.id}`], ['Key Address', data.key_address], ['VCAS Address', data.vcas_address], ['CP ID', String(data.cp_id ?? '')], ['Predefined', data.predefined ? 'Yes' : 'No']] as [string, string][]).map(([label, val]) => val ? (
                <div key={label}>
                  <div className={styles.fieldLabel}>{label}</div>
                  <div className={label.includes('Address') ? styles.fieldValueMono : styles.fieldValue}>{val}</div>
                </div>
              ) : null)}
              <div>
                <div className={styles.fieldLabel}>Status</div>
                <ActiveStatusPill value={data.status} />
              </div>
              {data.created ? (
                <div>
                  <div className={styles.fieldLabel}>Created</div>
                  <div className={styles.fieldValueMuted}>{new Date(data.created).toLocaleString()}</div>
                </div>
              ) : null}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add / edit dialog ────────────────────────────────────────────────────────

const KEY_ADDR_RE = /^http:\/\/[a-zA-Z0-9.-]+(:\d+)?$/;

function VerimatrixAddEditDialog({ open, onOpenChange, serviceId, config, editData, onDone }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  serviceId: string; config: VerimatrixDRMConfig;
  editData?: Verimatrix; onDone: () => void;
}) {
  const { show } = useToast();
  const isEdit = !!editData;
  const [form, setForm] = useState({ name: editData?.name ?? '', key_address: editData?.key_address ?? '', vcas_address: editData?.vcas_address ?? '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const create = useCreateVerimatrix(serviceId, config);
  const update = useUpdateVerimatrix(serviceId, config);
  const isPending = create.isPending || update.isPending;

  // Re-seed when editData changes (dialog reused).
  useEffect(() => {
    if (editData) setForm({ name: editData.name ?? '', key_address: editData.key_address ?? '', vcas_address: editData.vcas_address ?? '' });
    else setForm({ name: '', key_address: '', vcas_address: '' });
    setErrors({});
  }, [editData?.id, open]);

  const set = (k: string, v: string) => { setForm((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((p) => ({ ...p, [k]: '' })); };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.key_address.trim()) { e.key_address = 'Key Address is required'; }
    else if (!KEY_ADDR_RE.test(form.key_address.trim())) { e.key_address = 'Invalid format — e.g. http://example.com:80'; }
    if (!form.vcas_address.trim()) { e.vcas_address = 'VCAS Address is required'; }
    else if (!form.vcas_address.trim().startsWith('https://')) { e.vcas_address = 'VCAS Address must start with https://'; }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) { show({ tone: 'danger', title: 'Fix the highlighted fields.' }); return; }
    try {
      if (isEdit && editData?.id) {
        await update.mutateAsync({ id: editData.id, payload: { ...form } });
        show({ tone: 'success', title: 'Verimatrix DRM updated' });
      } else {
        await create.mutateAsync({ ...form });
        show({ tone: 'success', title: 'Verimatrix DRM created' });
      }
      onDone(); onOpenChange(false);
    } catch (e: unknown) { show({ tone: 'danger', title: (e as Error).message || 'Operation failed' }); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? config.labels.updateDialogTitle : config.labels.addDialogTitle}</DialogTitle>
          <DialogDescription>{isEdit ? config.labels.updateDialogDescription : config.labels.addDialogDescription}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className={styles.detailContent}>
            <Field label="Name" required error={errors.name}>
              {(fp) => <Input {...fp} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="verimatrix_drm" />}
            </Field>
            <Field label="Key Address" required description="Must start with http:// and may include an optional port." error={errors.key_address}>
              {(fp) => <Input {...fp} value={form.key_address} onChange={(e) => set('key_address', e.target.value)} placeholder="http://28.1.0.4:21" />}
            </Field>
            <Field label="VCAS Address" required description="Must be a valid HTTPS URL." error={errors.vcas_address}>
              {(fp) => <Input {...fp} value={form.vcas_address} onChange={(e) => set('vcas_address', e.target.value)} placeholder="https://verimatrix.dev" />}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} disabled={isPending}>{isPending ? 'Saving…' : isEdit ? 'Update DRM' : 'Create DRM'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── List ─────────────────────────────────────────────────────────────────────

// Columns the user can toggle via "Display options" (name + actions are fixed).
const TOGGLEABLE = [
  { id: 'id', label: 'ID' },
  { id: 'key_address', label: 'Key Address' },
  { id: 'vcas_address', label: 'VCAS Address' },
  { id: 'status', label: 'Status' },
  { id: 'predefined', label: 'Predefined' },
  { id: 'created', label: 'Created' },
];
const DEFAULT_VISIBLE: Record<string, boolean> = { id: true, key_address: true, vcas_address: true, status: true, predefined: true, created: true };

/** Download the given Verimatrix DRM entries as a CSV file. */
function exportCsv(rows: Verimatrix[]) {
  const head = ['id', 'name', 'key_address', 'vcas_address', 'status', 'predefined', 'created'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((r) =>
      [r.id, r.name, r.key_address, r.vcas_address, r.status === 1 ? 'active' : 'inactive', r.predefined ? 'yes' : 'no', r.created ?? '']
        .map(esc)
        .join(','),
    )
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `verimatrix-drm-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export interface VerimatrixDrmListProps {
  serviceId: string;
  config: VerimatrixDRMConfig;
  /** Console-specific breadcrumb (root differs per console: Live Transcoding vs Cloud Encoding). */
  breadcrumbs?: ReactNode;
}

/** Config-driven Verimatrix DRM list for Cloud Encoding and Live Transcoding. */
export function VerimatrixDrmList({ serviceId, config, breadcrumbs }: VerimatrixDrmListProps) {
  const { show } = useToast();

  const { data: verimatrixes = [], isLoading, refetch } = useVerimatrixes(serviceId, config);
  const deleteMut = useDeleteVerimatrix(serviceId, config);
  const loading = isLoading;

  const [addOpen, setAddOpen] = useState(false);
  const [editData, setEditData] = useState<Verimatrix | undefined>();
  const [detailId, setDetailId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Verimatrix | null>(null);
  const [search, setSearch] = useState('');
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);

  // Active scope derived from the FilterBuilder conditions (multiselect values
  // are comma-joined per row, so split + flatten across matching rows).
  const collect = (field: string) =>
    conditions
      .filter((c) => c.field === field)
      .flatMap((c) => (c.value || '').split(','))
      .map((s) => s.trim())
      .filter(Boolean);
  const statusFilters = useMemo(() => new Set(collect('status')), [conditions]);
  const predefinedFilters = useMemo(() => new Set(collect('predefined')), [conditions]);

  const filterFields: FilterField[] = [
    {
      value: 'status',
      label: 'Status',
      input: 'multiselect',
      operators: [{ value: 'is_any_of', label: 'is any of' }],
      options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }],
    },
    {
      value: 'predefined',
      label: 'Predefined',
      input: 'multiselect',
      operators: [{ value: 'is_any_of', label: 'is any of' }],
      options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }],
    },
  ];

  const rows = useMemo(() => {
    let r = verimatrixes;
    if (statusFilters.size) r = r.filter((x) => statusFilters.has(x.status === 1 ? 'active' : 'inactive'));
    if (predefinedFilters.size) r = r.filter((x) => predefinedFilters.has(x.predefined ? 'yes' : 'no'));
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) => x.name.toLowerCase().includes(q) || (x.key_address ?? '').toLowerCase().includes(q));
    }
    return r;
  }, [verimatrixes, statusFilters, predefinedFilters, search]);

  const columns = useMemo<ColumnDef<Verimatrix, unknown>[]>(() => {
    const all: Record<string, ColumnDef<Verimatrix, unknown>> = {
      id: { id: 'id', header: 'ID', cell: ({ row }) => <Button variant="ghost" size="sm" onClick={() => setDetailId(row.original.id)}>#{row.original.id}</Button> },
      key_address: { id: 'key_address', header: 'Key Address', cell: ({ row }) => <span className={styles.cellMono}>{row.original.key_address}</span> },
      vcas_address: { id: 'vcas_address', header: 'VCAS Address', cell: ({ row }) => <span className={styles.cellMono}>{row.original.vcas_address}</span> },
      status: { id: 'status', header: 'Status', cell: ({ row }) => <ActiveStatusPill value={row.original.status} /> },
      // Predefined is a category label, not a status — neutral/subtle Badge.
      predefined: { id: 'predefined', header: 'Predefined', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.predefined ? 'Yes' : 'No'}</Badge> },
      created: { id: 'created', header: 'Created', cell: ({ row }) => <span className={styles.cellMuted}>{row.original.created ? new Date(row.original.created).toLocaleString() : '—'}</span> },
    };
    const nameCol: ColumnDef<Verimatrix, unknown> = { id: 'name', header: 'Name', cell: ({ row }) => <span className={styles.cellBold}>{row.original.name}</span> };
    const actionsCol: ColumnDef<Verimatrix, unknown> = {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <RowMenu
          items={[
            { label: 'Edit', onSelect: () => { setEditData(row.original); setAddOpen(true); } },
            { label: 'Delete', danger: true, onSelect: () => setPendingDelete(row.original) },
          ]}
        />
      ),
    };
    return [nameCol, ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]), actionsCol];
  }, [visible]);

  return (
    <>
      <Container size="xl">
        <PageHeader
          breadcrumbs={breadcrumbs}
          title={`${config.labels.pageTitle}${verimatrixes.length ? ` (${verimatrixes.length})` : ''}`}
          description={config.labels.pageDescription}
          actions={<Button startIcon={<Plus size={15} />} onClick={() => { setEditData(undefined); setAddOpen(true); }}>{config.labels.createButtonText}</Button>}
        />

        <Toolbar
          start={
            <Input
              placeholder="Search by name or key address…"
              startSlot={<Search size={15} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
          }
          end={
            <>
              <RefreshButton onRefresh={() => void refetch()} />
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="secondary" startIcon={<Filter size={15} />}>
                    Filters{conditions.length ? ` (${conditions.length})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className={styles.filterPopoverContent}>
                  <FilterBuilder
                    fields={filterFields}
                    defaultValue={conditions.length ? conditions : undefined}
                    onApply={(c) => {
                      setConditions(c.filter((x) => x.value.trim()));
                      setFilterOpen(false);
                    }}
                    onClose={() => setFilterOpen(false)}
                  />
                </PopoverContent>
              </Popover>
              {conditions.length > 0 && (
                <Button variant="ghost" onClick={() => setConditions([])}>Clear</Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>Display options</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {TOGGLEABLE.map((c) => (
                    <DropdownMenuCheckboxItem
                      key={c.id}
                      checked={visible[c.id]}
                      onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {c.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="secondary"
                startIcon={<Download size={15} />}
                disabled={rows.length === 0}
                onClick={() => exportCsv(rows)}
              >
                Export
              </Button>
            </>
          }
        />

        {loading ? (
          <div className={styles.skeletonGrid}>
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.id)}
            onRowClick={(row) => setDetailId(row.id)}
            emptyState={
              // First-use shows a creation hint; once entries exist the empty
              // state means the search/filters excluded everything.
              verimatrixes.length === 0 ? (
                <EmptyState
                  icon={<ShieldCheck size={20} />}
                  title="No Verimatrix DRM entries"
                  description="Add a Verimatrix DRM endpoint to enable content protection."
                  actions={
                    <Button startIcon={<Plus size={14} />} onClick={() => { setEditData(undefined); setAddOpen(true); }}>
                      {config.labels.createButtonText}
                    </Button>
                  }
                />
              ) : (
                'No Verimatrix DRM entries match your search or filters.'
              )
            }
            footer={verimatrixes.length ? `Showing ${rows.length} of ${verimatrixes.length}` : undefined}
          />
        )}
      </Container>

      <VerimatrixAddEditDialog
        open={addOpen}
        onOpenChange={(v) => { setAddOpen(v); if (!v) setEditData(undefined); }}
        serviceId={serviceId}
        config={config}
        editData={editData}
        onDone={() => setAddOpen(false)}
      />
      <VerimatrixDetailDialog serviceId={serviceId} id={detailId} config={config} open={!!detailId} onOpenChange={(v) => { if (!v) setDetailId(null); }} />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(v) => { if (!v) setPendingDelete(null); }}
        title={config.labels.deleteDialogTitle}
        description={config.labels.deleteDialogDescription(pendingDelete?.name ?? '')}
        confirmLabel="Delete"
        destructive
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMut.mutate({ id: pendingDelete.id }, {
            onSuccess: () => { show({ tone: 'success', title: 'Verimatrix DRM deleted' }); setPendingDelete(null); },
            onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to delete Verimatrix DRM' }),
          });
        }}
      />
    </>
  );
}
